"use client"

import React, { RefObject } from "react"
import { TIconButton } from "../button/TIconButton"
import { Dialog, DialogDescription, DialogFullscreenContent, DialogTitle, DialogTrigger } from "../shadcn/modal"

/**
 * @description 전체화면 모달. children은 반드시 function으로 { onClose }를 받음
 * @example
 * <TFullscreenModal open={isOpen} onOpenChange={setIsOpen}>
 *   {({ onClose }) => (
 *     <div>
 *       <h2>전체화면 콘텐츠</h2>
 *       <button onClick={onClose}>닫기</button>
 *     </div>
 *   )}
 * </TFullscreenModal>
 */
interface TFullscreenModalProps {
	/** 모달 열림 상태 */
	open: boolean
	/** 열림 상태 변경 콜백 */
	onOpenChange: (open: boolean) => void
	/** 모달 콘텐츠 (render function, onClose를 인자로 받음) */
	children: (props: { onClose: () => void }) => React.ReactNode
	/** 모달 트리거 요소 */
	trigger?: React.ReactNode
	/** 우측 상단 닫기 버튼 숨김 */
	hiddenClose?: boolean
	/**
	 * 닫기 버튼의 접근성 라벨. 패키지는 앱의 i18n에 접근할 수 없으므로 앱이 번역된 문자열을
	 * 직접 주입할 수 있게 한다.
	 * @default "close button" (TIconButton 기본값)
	 */
	closeLabel?: string
	/** DialogContent DOM 참조 */
	dialogContentRef?: RefObject<HTMLDivElement>
	/**
	 * 모달 open 시 자동 focus 동작 override.
	 * preventDefault() 호출 시 기본 첫 focusable 자동 focus 비활성화 — Radix Tooltip 등 focus-triggered UI 가
	 * 모달 진입 즉시 자동 표시되는 회귀 차단용.
	 */
	onOpenAutoFocus?: (event: Event) => void
}

export function TFullscreenModal(props: TFullscreenModalProps) {
	const { open, onOpenChange, children, trigger, hiddenClose, closeLabel, dialogContentRef, onOpenAutoFocus } = props

	const handleClose = () => onOpenChange(false)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
			<DialogFullscreenContent ref={dialogContentRef} onOpenAutoFocus={onOpenAutoFocus}>
				<DialogTitle className="sr-only"></DialogTitle>
				<DialogDescription className="sr-only"></DialogDescription>
				<div className="contents-wrap">{children({ onClose: handleClose })}</div>
				{!hiddenClose && (
					<div className="close">
						<TIconButton name="close" color="tertiary" size="medium" onClick={handleClose} aria-label={closeLabel} />
					</div>
				)}
			</DialogFullscreenContent>
		</Dialog>
	)
}

TFullscreenModal.displayName = "TFullscreenModal"
